import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { ShieldCheck, UserCog, Store, HelpCircle, Key } from "lucide-react";

export default function AdminPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pwdModal, setPwdModal] = useState(null); // {id, name}
  const [pwdVal, setPwdVal] = useState("");

  const load = async () => {
    try { const r = await api.get("/admin/users"); setUsers(r.data); }
    catch { toast.error("Admin only"); }
  };
  useEffect(() => { load(); }, []);

  const setRole = async (id, role) => {
    try { await api.post(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeUser = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"? This also deletes their characters and campaigns they GM. This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitPwd = async () => {
    if (!pwdVal || !pwdModal) return;
    try {
      await api.post(`/admin/users/${pwdModal.id}/password`, { password: pwdVal });
      toast.success(`Password reset for ${pwdModal.name}`);
      setPwdModal(null); setPwdVal("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (user?.role !== "admin") return <div className="p-8 mono text-[#FF2A2A]">// Access denied</div>;

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="heading-mono text-4xl amber amber-glow uppercase">// Admin Panel</h1>
        <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-widest uppercase">W-Y Root Access Terminal</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link to="/store" className="panel p-5 hover:shadow-[0_0_18px_rgba(255,176,0,0.3)]">
          <Store className="amber mb-2" size={22} strokeWidth={1.5} />
          <div className="heading-mono amber uppercase">Store Items</div>
          <div className="mono text-xs text-[#A0A0A0] mt-1">Manage weapons, armor, gear, vehicles</div>
        </Link>
        <Link to="/admin/questions" className="panel p-5 hover:shadow-[0_0_18px_rgba(255,176,0,0.3)]">
          <HelpCircle className="amber mb-2" size={22} strokeWidth={1.5} />
          <div className="heading-mono amber uppercase">XP Questions</div>
          <div className="mono text-xs text-[#A0A0A0] mt-1">Global XP questionnaire</div>
        </Link>
        <div className="panel p-5">
          <ShieldCheck className="amber mb-2" size={22} strokeWidth={1.5} />
          <div className="heading-mono amber uppercase">Crew ({users.length})</div>
          <div className="mono text-xs text-[#A0A0A0] mt-1">Role management (below)</div>
        </div>
      </div>

      <div className="panel p-5" data-testid="admin-users-table">
        <div className="label-term amber mb-4">// User Management</div>
        <table className="w-full mono text-sm">
          <thead>
            <tr className="text-left text-[#A0A0A0] text-[10px] uppercase tracking-widest">
              <th className="p-2 border-b border-[#333]">Username</th>
              <th className="p-2 border-b border-[#333]">Email</th>
              <th className="p-2 border-b border-[#333]">Role</th>
              <th className="p-2 border-b border-[#333] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                <td className="p-2 border-b border-[#222] amber">{u.username}</td>
                <td className="p-2 border-b border-[#222] text-[#A0A0A0]">{u.email}</td>
                <td className="p-2 border-b border-[#222]">
                  <span className={`text-[10px] px-2 py-0.5 border ${u.role==="admin"?"border-[#4AF626] neon-green":"border-[#555] text-[#A0A0A0]"}`}>{u.role?.toUpperCase()}</span>
                </td>
                <td className="p-2 border-b border-[#222] text-right">
                  <div className="flex gap-2 justify-end">
                    {u.role === "admin" ? (
                      <button className="btn-amber text-xs" onClick={()=>setRole(u.id,"player")} data-testid={`demote-${u.id}`}>Demote</button>
                    ) : (
                      <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>setRole(u.id,"admin")} data-testid={`promote-${u.id}`}>
                        <UserCog size={12}/> Make Admin
                      </button>
                    )}
                    {u.id !== user.id && (
                      <>
                        <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>{ setPwdModal({id: u.id, name: u.username}); setPwdVal(""); }} data-testid={`reset-pwd-${u.id}`}><Key size={12}/> Reset Password</button>
                        <button className="text-[#FF2A2A] text-xs border border-[#FF2A2A] px-2 py-1" onClick={()=>removeUser(u.id, u.username)} data-testid={`delete-user-${u.id}`}>Delete</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pwdModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" data-testid="reset-pwd-modal">
          <div className="panel p-6 w-full max-w-md mx-4 border border-[#FFB000]">
            <div className="heading-mono amber uppercase mb-1">// Reset Password</div>
            <div className="mono text-xs text-[#A0A0A0] mb-4">User: <span className="amber">{pwdModal.name}</span></div>
            <label className="label-term mb-1 block">New Password</label>
            <input
              className="input-term mb-2"
              type="text"
              value={pwdVal}
              onChange={e => setPwdVal(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitPwd()}
              placeholder="≥8 chars, 1 uppercase, 1 number"
              autoFocus
              data-testid="reset-pwd-input"
            />
            <div className="flex gap-2 justify-end mt-3">
              <button className="btn-amber text-xs" onClick={() => { setPwdModal(null); setPwdVal(""); }} data-testid="reset-pwd-cancel">Cancel</button>
              <button className="btn-amber text-xs" onClick={submitPwd} disabled={!pwdVal} data-testid="reset-pwd-submit">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
